import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/cn'
import { useTelemetry } from '../telemetry/useTelemetry'
import { NAV_SURFACES } from './migration'
import { SurfaceIcon } from './SurfaceIcon'

/**
 * Desktop nav rail — Void FUI. Live surfaces render at full strength;
 * upcoming ones are dimmed but stay clickable (R22): the click lands on
 * the interstitial, and hover explains it.
 */
export function NavRailV2() {
  const { pathname } = useLocation()
  const { inboxUnread, mailUnread } = useTelemetry()

  // Longest matching navTo wins, so a nested entry (/office/pdfcn) doesn't
  // also light its parent (/office) — mirrors migration.ts surfaceForPath.
  const activeTo =
    pathname === '/'
      ? '/'
      : NAV_SURFACES.filter((s) => s.navTo !== '/' && pathname.startsWith(s.navTo)).sort(
          (a, b) => b.navTo.length - a.navTo.length,
        )[0]?.navTo
  const isActive = (to: string) => to === activeTo

  return (
    <aside className="v2-rail" aria-label="Primary navigation">
      <div className="v2-brand" aria-hidden="true">
        <span>T</span>
        <span className="v2-brand-pulse" />
      </div>
      <nav className="v2-rail-items">
        {NAV_SURFACES.map((s) => {
          if (s.external) {
            // Full page load out of the SPA (the app at navTo is Caddy-routed,
            // not a router route) — so no Link, no active state, no drag.
            return (
              <a key={s.id} href={s.navTo} className="v2-rail-item">
                <SurfaceIcon id={s.id} />
                <span className="v2-rail-label">{s.label}</span>
              </a>
            )
          }
          const active = isActive(s.navTo)
          const live = s.status === 'live'
          const badge =
            s.id === 'inbox' && inboxUnread ? inboxUnread : s.id === 'mail' && mailUnread ? mailUnread : undefined
          return (
            <Link
              key={s.id}
              to={s.navTo}
              className={cn('v2-rail-item', active && 'active', !live && 'dim')}
              aria-current={active ? 'page' : undefined}
              data-tip={live ? undefined : 'Coming in V2 — opens in V1'}
              draggable
              onDragStart={(e) => e.dataTransfer.setData('text/x-hub-path', s.navTo)}
            >
              <SurfaceIcon id={s.id} />
              <span className="v2-rail-label">{s.label}</span>
              {badge ? <span className="v2-rail-badge">{badge > 99 ? '99+' : badge}</span> : null}
            </Link>
          )
        })}
      </nav>
      <div className="v2-rail-foot">
        <span className="fui-microtag">v2</span>
      </div>
    </aside>
  )
}
